import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';

const BASE = process.env.REACT_APP_API_URL || '/api';

const STATIC_PLANS = [
  { id: 'free', name: 'Free', price: 0, color: '#A3B4CC', popular: false,
    features: ['5 books access', 'Basic search', 'Read online only', 'Community support'] },
  { id: 'pro', name: 'Individual Pro', price: 4999, color: '#C97B77', popular: true,
    features: ['All 428+ books', 'Full-text search', 'Offline downloads', 'LexAssist AI', 'Annotations & Notebooks', '3 device sync'] },
  { id: 'stud', name: 'Student Pro', price: 1999, color: '#7A9E8E', popular: false,
    features: ['All books access', 'Full search', 'Offline downloads', '2 device sync', 'Student verification required'] },
  { id: 'inst', name: 'Institutional', price: null, color: '#9B8EC4', popular: false,
    features: ['Unlimited seats', 'Admin dashboard', 'SSO integration', 'Usage analytics', 'Dedicated support'] },
];

const PricingPage = () => {
  const [billing, setBilling]         = useState('annual');
  const [groupPlans, setGroupPlans]   = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [toast, setToast]             = useState(null);
  const { currentUser, getToken }     = useApp();

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/groups/plans`);
        const data = await res.json();
        setGroupPlans(data.data || []);
      } catch { setGroupPlans([]); }
      finally { setLoadingPlans(false); }
    })();
  }, []);

  const isUserInGroup = (group) => {
    const isPaid = group.price && group.price > 0;
    if (isPaid) {
      // Paid group — sirf paidGroupIds check karo
      return (currentUser?.paidGroupIds || []).some(id => String(id) === String(group._id));
    } else {
      // Free group — groupIds check karo
      return (currentUser?.groupIds || []).some(id => String(id) === String(group._id));
    }
  };

  const handleGroupSubscribe = async (group) => {
    if (!currentUser) { showToast('Please login first', 'error'); return; }
    if (isUserInGroup(group)) { showToast('You are already subscribed!', 'error'); return; }

    setSubscribing(group._id);
    try {
      const token = await getToken();
      if (group.price && group.price > 0) {
        // Paid group — simulate payment
        const confirmed = window.confirm(
          `Subscribe to "${group.name}" for ₹${group.price}?\n\nPayment gateway integration coming soon.\nClick OK to proceed with demo subscription.`
        );
        if (!confirmed) { setSubscribing(null); return; }
      }

      const res = await fetch(`${BASE}/groups/${group._id}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.success) {
        if (data.already) {
          showToast('You are already subscribed!');
        } else {
          showToast(`🎉 Successfully subscribed to "${group.name}"! Refresh to see your books.`);
          // Reload page after 2s to reflect new books
          setTimeout(() => window.location.reload(), 2000);
        }
      } else {
        showToast(data.message || 'Failed to subscribe', 'error');
      }
    } catch { showToast('Something went wrong. Please try again.', 'error'); }
    finally { setSubscribing(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 pb-20 animate-fade-in" style={{ fontFamily: 'Nunito,system-ui,sans-serif' }}>
      <div className="text-center mb-8">
        <div className="font-bold uppercase mb-2" style={{ fontSize: 10, color: 'var(--rose)', letterSpacing: 3 }}>TRANSPARENT PRICING</div>
        <h1 className="font-display font-bold mb-3" style={{ fontFamily: 'Playfair Display,serif', fontSize: 28, color: 'var(--ink)' }}>
          Choose your <em style={{ color: 'var(--rose)' }}>plan</em>
        </h1>
        <p style={{ fontSize: 13, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>
          Significantly below EBC Reader Platinum — superior content, better price.
        </p>
        <div className="flex items-center justify-center gap-2 mt-4">
          {['annual', 'monthly'].map(b => (
            <button key={b} onClick={() => setBilling(b)}
              className="px-5 py-2 rounded-full font-bold capitalize border cursor-pointer transition-all"
              style={{ fontSize: 12, fontFamily: 'Nunito,sans-serif', background: billing === b ? 'var(--rose)' : 'transparent', color: billing === b ? 'white' : 'var(--ink-mid)', borderColor: billing === b ? 'var(--rose)' : 'var(--border)' }}>
              {b}{b === 'annual' && <span style={{ fontSize: 9, opacity: .75 }}> Save 20%</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Static Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {STATIC_PLANS.map(plan => (
          <div key={plan.id} className="relative bg-white rounded-2xl border flex flex-col p-5 transition-all hover:-translate-y-1 hover:shadow-md"
            style={{ borderColor: plan.popular ? 'var(--rose)' : 'var(--border)', boxShadow: plan.popular ? 'var(--shadow-sm),0 0 0 2px rgba(201,123,119,.2)' : 'var(--shadow-xs)' }}>
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-white rounded-full font-bold px-4 py-1 whitespace-nowrap"
                style={{ background: 'var(--rose)', fontSize: 9, fontFamily: 'Nunito,sans-serif' }}>MOST POPULAR</div>
            )}
            <div className="rounded-xl mb-3 flex items-center justify-center text-white font-bold"
              style={{ width: 40, height: 40, background: plan.color, fontSize: 18, fontFamily: 'Playfair Display,serif' }}>
              {plan.name[0]}
            </div>
            <div className="font-bold mb-1" style={{ fontSize: 13, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>{plan.name}</div>
            {plan.price !== null ? (
              <div className="mb-4">
                <span className="font-display font-bold" style={{ fontSize: 28, color: 'var(--ink)', fontFamily: 'Playfair Display,serif' }}>
                  ₹{billing === 'annual' ? plan.price.toLocaleString() : Math.round(plan.price * 1.25 / 12).toLocaleString()}
                </span>
                <span style={{ fontSize: 11, color: 'var(--ink-lt)', fontFamily: 'Nunito,sans-serif' }}>/{billing === 'annual' ? 'year' : 'month'}</span>
              </div>
            ) : (
              <div className="font-display font-bold mb-4" style={{ fontSize: 22, color: 'var(--ink)', fontFamily: 'Playfair Display,serif' }}>Custom</div>
            )}
            <div className="space-y-2 flex-1 mb-5">
              {plan.features.map((f, i) => (
                <div key={i} className="flex items-start gap-2" style={{ fontSize: 11, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>
                  <span style={{ color: 'var(--sage)', marginTop: 2, flexShrink: 0 }}>✓</span><span>{f}</span>
                </div>
              ))}
            </div>
            <button className="w-full py-3 rounded-xl font-bold border cursor-pointer transition-all"
              style={{ fontSize: 12, fontFamily: 'Nunito,sans-serif', background: plan.popular ? 'var(--rose)' : 'transparent', color: plan.popular ? 'white' : 'var(--ink-mid)', borderColor: plan.popular ? 'var(--rose)' : 'var(--border)' }}>
              {plan.price === null ? 'Contact Sales' : plan.price === 0 ? 'Get Started Free' : 'Subscribe Now'}
            </button>
          </div>
        ))}
      </div>

      {/* Group Plans */}
      {!loadingPlans && groupPlans.length > 0 && (
        <div>
          <div className="text-center mb-6">
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--rose)', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 6 }}>SPECIAL ACCESS</div>
            <h2 style={{ fontFamily: 'Playfair Display,serif', fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>
              Group <em style={{ color: 'var(--rose)' }}>Plans</em>
            </h2>
            <p style={{ fontSize: 12, color: 'var(--ink-lt)', marginTop: 4 }}>Curated book collections for specific needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {groupPlans.map(group => {
              const isFree    = !group.price || group.price === 0;
              const isJoined  = isUserInGroup(group);
              const isLoading = subscribing === group._id;

              return (
                <div key={group._id} className="bg-white rounded-2xl border flex flex-col p-5 transition-all hover:-translate-y-1 hover:shadow-md"
                  style={{ borderColor: isJoined ? 'var(--sage)' : 'var(--border)', boxShadow: isJoined ? '0 0 0 2px rgba(122,158,142,.2)' : 'var(--shadow-xs)' }}>

                  {/* Badge */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div className="rounded-xl flex items-center justify-center text-white font-bold"
                      style={{ width: 40, height: 40, background: isFree ? 'var(--sage)' : 'var(--rose)', fontSize: 16 }}>
                      {isFree ? '🎁' : '💎'}
                    </div>
                    {isJoined && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 8, background: 'var(--sage-pale)', color: 'var(--sage)' }}>
                        ✓ Subscribed
                      </span>
                    )}
                  </div>

                  <div className="font-bold mb-1" style={{ fontSize: 14, color: 'var(--ink)', fontFamily: 'Nunito,sans-serif' }}>{group.name}</div>

                  {/* Price */}
                  <div className="mb-3">
                    {isFree ? (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--sage)', fontFamily: 'Playfair Display,serif' }}>FREE</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span style={{ fontSize: 28, fontWeight: 700, color: 'var(--ink)', fontFamily: 'Playfair Display,serif' }}>₹{group.price}</span>
                        <span style={{ fontSize: 11, color: 'var(--ink-lt)' }}>/one-time</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 mb-5">
                    <div style={{ fontSize: 11, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>
                      ✓ Access to curated book collection
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif', marginTop: 4 }}>
                      ✓ Books visible in My Library
                    </div>
                    {isFree && (
                      <div style={{ fontSize: 11, color: 'var(--sage)', fontFamily: 'Nunito,sans-serif', marginTop: 4, fontWeight: 700 }}>
                        ✓ Instant access — no payment needed
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleGroupSubscribe(group)}
                    disabled={isJoined || isLoading}
                    style={{
                      width: '100%', padding: '12px', borderRadius: 12, fontWeight: 700, cursor: isJoined ? 'default' : 'pointer',
                      fontSize: 12, fontFamily: 'Nunito,sans-serif', border: 'none', transition: 'all 0.2s',
                      background: isJoined ? 'var(--sage-pale)' : isFree ? 'var(--sage)' : 'var(--rose)',
                      color: isJoined ? 'var(--sage)' : 'white',
                      opacity: isLoading ? 0.7 : 1,
                    }}>
                    {isLoading ? 'Processing…' : isJoined ? '✓ Already Subscribed' : isFree ? 'Get Free Access' : `Subscribe for ₹${group.price}`}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom note */}
      <div className="mt-8 rounded-xl p-5 text-center border"
        style={{ background: 'var(--sage-pale)', borderColor: 'rgba(122,158,142,.2)' }}>
        <div className="font-bold mb-1" style={{ fontSize: 13, color: 'var(--sage)', fontFamily: 'Nunito,sans-serif' }}>
          ✓ 30-day money-back guarantee · No hidden fees · DPDP Act 2023 compliant
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-mid)', fontFamily: 'Nunito,sans-serif' }}>
          All plans include: offline access · AIR auto-citation · Annotation suite · Multi-device sync
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          background: toast.type === 'error' ? '#DC2626' : 'var(--sage)', color: 'white',
          padding: '12px 24px', borderRadius: 12, fontSize: 13, fontWeight: 700,
          fontFamily: 'Nunito,sans-serif', boxShadow: '0 4px 20px rgba(0,0,0,.2)', whiteSpace: 'nowrap', maxWidth: '90vw' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export { PricingPage };
export default PricingPage;